import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, SignOut, Briefcase, UsersThree, EnvelopeSimple, ChatCircleDots } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { INDUSTRIES } from "@/components/site/content";

const emptyJob = {
  title: "", location: "", salary: "", experience: "", qualification: "",
  employment_type: "Full-time", shift: "Day", industry: "", description: "", is_active: true,
};

function formatErr(detail) {
  if (!detail) return "Request failed.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminDashboardPage() {
  const { user, loading, authHeaders, logout, API } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [empEnq, setEmpEnq] = useState([]);
  const [apps, setApps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, j, e, a, c] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders }),
        axios.get(`${API}/admin/jobs`, { headers: authHeaders }),
        axios.get(`${API}/admin/employer-enquiries`, { headers: authHeaders }),
        axios.get(`${API}/admin/job-applications`, { headers: authHeaders }),
        axios.get(`${API}/admin/contact-enquiries`, { headers: authHeaders }),
      ]);
      setStats(s.data); setJobs(j.data); setEmpEnq(e.data); setApps(a.data); setContacts(c.data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  }, [API, authHeaders]);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-muted-foreground">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const onSaveJob = async (form) => {
    try {
      if (form.id) {
        const { id, created_at, ...body } = form;
        await axios.patch(`${API}/admin/jobs/${id}`, body, { headers: authHeaders });
        toast.success("Job updated");
      } else {
        await axios.post(`${API}/admin/jobs`, form, { headers: authHeaders });
        toast.success("Job created");
      }
      setDialogOpen(false); setEditing(null); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    try {
      await axios.delete(`${API}/admin/jobs/${id}`, { headers: authHeaders });
      toast.success("Job deleted"); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const toggleActive = async (job) => {
    try {
      await axios.patch(`${API}/admin/jobs/${job.id}`, { is_active: !job.is_active }, { headers: authHeaders });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-secondary/30">
      <div className="bg-navy-900 text-white">
        <div className="container-tight py-8 flex items-center justify-between gap-4">
          <div>
            <p className="overline text-gold-400">Admin</p>
            <h1 className="font-serif text-3xl mt-1">CRETRE Dashboard</h1>
            <p className="text-white/60 text-sm mt-1">Signed in as {user.email}</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="admin-goto-site" variant="outline" onClick={() => navigate("/")} className="rounded-full h-10 bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white">View Site</Button>
            <Button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/"); }} className="rounded-full h-10 bg-gold-500 hover:bg-gold-600 text-white">
              <SignOut size={16} className="mr-1" /> Sign Out
            </Button>
          </div>
        </div>
      </div>

      <div className="container-tight py-10">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard icon={Briefcase} label="Total Jobs" value={stats?.jobs ?? "—"} />
          <StatCard icon={Briefcase} label="Active Jobs" value={stats?.active_jobs ?? "—"} accent />
          <StatCard icon={UsersThree} label="Employer Enquiries" value={stats?.employer_enquiries ?? "—"} />
          <StatCard icon={EnvelopeSimple} label="Job Applications" value={stats?.job_applications ?? "—"} />
          <StatCard icon={ChatCircleDots} label="Contact Enquiries" value={stats?.contact_enquiries ?? "—"} />
        </div>

        <Tabs defaultValue="jobs" className="mt-10">
          <TabsList data-testid="admin-tabs" className="bg-white border border-border">
            <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
            <TabsTrigger value="employers" data-testid="tab-employers">Employer Enquiries</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>
            <TabsTrigger value="contact" data-testid="tab-contact">Contact</TabsTrigger>
          </TabsList>

          <TabsContent value="jobs" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-navy-800">Manage Jobs</h3>
              <Button data-testid="admin-add-job-btn" onClick={() => { setEditing({ ...emptyJob }); setDialogOpen(true); }} className="bg-navy-800 hover:bg-navy-700 text-white rounded-full h-10">
                <Plus size={16} className="mr-1" /> Add Job
              </Button>
            </div>
            <div className="grid gap-3">
              {jobs.map((j) => (
                <div key={j.id} data-testid={`admin-job-${j.id}`} className="bg-white border border-border rounded-xl p-5 grid md:grid-cols-[1fr,auto] gap-4 items-center">
                  <div>
                    <div className="flex items-center gap-3">
                      <h4 className="font-serif text-lg text-navy-800">{j.title}</h4>
                      <Badge variant={j.is_active ? "default" : "secondary"} className={j.is_active ? "bg-emerald-600 text-white" : "bg-muted"}>
                        {j.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{j.location} · {j.industry} · {j.employment_type}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 mr-2">
                      <Switch checked={j.is_active} onCheckedChange={() => toggleActive(j)} data-testid={`admin-toggle-${j.id}`} />
                      <span className="text-xs text-muted-foreground">Active</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => { setEditing(j); setDialogOpen(true); }} data-testid={`admin-edit-${j.id}`}>
                      <PencilSimple size={14} /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => onDelete(j.id)} data-testid={`admin-delete-${j.id}`} className="text-red-600 hover:text-red-700">
                      <Trash size={14} /> Delete
                    </Button>
                  </div>
                </div>
              ))}
              {jobs.length === 0 && <p className="text-muted-foreground text-sm">No jobs yet. Add your first job.</p>}
            </div>
          </TabsContent>

          <TabsContent value="employers" className="mt-6">
            <List data={empEnq} render={(e) => (
              <>
                <h4 className="font-serif text-lg text-navy-800">{e.company_name}</h4>
                <p className="text-xs text-muted-foreground mt-1">{e.contact_person} · {e.designation} · {e.positions} position(s)</p>
                <p className="text-sm mt-2">📞 {e.phone} · ✉ {e.email} · 📍 {e.location}</p>
                <p className="text-sm mt-1 text-muted-foreground">{e.industry} · {e.employment_type} · CTC {e.salary}</p>
                {e.additional && <p className="text-sm mt-2 text-navy-800">{e.additional}</p>}
              </>
            )} empty="No employer enquiries yet." />
          </TabsContent>

          <TabsContent value="applications" className="mt-6">
            <List data={apps} render={(a) => (
              <>
                <h4 className="font-serif text-lg text-navy-800">{a.full_name} <span className="text-sm font-sans text-muted-foreground">— {a.position_applied}</span></h4>
                <p className="text-sm mt-1">📞 {a.mobile} · ✉ {a.email}</p>
                <p className="text-sm mt-1 text-muted-foreground">{a.current_location} → {a.preferred_location || "—"} · {a.experience} · {a.qualification}</p>
                {a.current_company && <p className="text-sm mt-1 text-muted-foreground">Company: {a.current_company}</p>}
              </>
            )} empty="No job applications yet." />
          </TabsContent>

          <TabsContent value="contact" className="mt-6">
            <List data={contacts} render={(c) => (
              <>
                <h4 className="font-serif text-lg text-navy-800">{c.name}{c.company ? ` — ${c.company}` : ""}</h4>
                <p className="text-sm mt-1">📞 {c.phone} · ✉ {c.email}</p>
                {c.requirement && <p className="text-sm mt-1 text-muted-foreground">Requirement: {c.requirement}</p>}
                <p className="text-sm mt-2 text-navy-800">{c.message}</p>
              </>
            )} empty="No contact enquiries yet." />
          </TabsContent>
        </Tabs>
      </div>

      <JobDialog open={dialogOpen} onOpenChange={setDialogOpen} value={editing} onSave={onSaveJob} onClose={() => { setDialogOpen(false); setEditing(null); }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent ? "bg-navy-800 text-white border-navy-800" : "bg-white border-border"}`}>
      <Icon size={22} weight="duotone" className={accent ? "text-gold-400" : "text-navy-800"} />
      <p className={`mt-3 text-xs uppercase tracking-wide ${accent ? "text-white/70" : "text-muted-foreground"}`}>{label}</p>
      <p className={`mt-1 font-serif text-3xl ${accent ? "text-white" : "text-navy-800"}`}>{value}</p>
    </div>
  );
}

function List({ data, render, empty }) {
  if (!data || data.length === 0) return <p className="text-muted-foreground text-sm">{empty}</p>;
  return (
    <div className="grid gap-3">
      {data.map((row) => (
        <div key={row.id} data-testid={`admin-row-${row.id}`} className="bg-white border border-border rounded-xl p-5">
          {render(row)}
          <p className="text-[11px] text-muted-foreground mt-3">{new Date(row.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

function JobDialog({ open, onOpenChange, value, onSave, onClose }) {
  const [form, setForm] = useState(emptyJob);
  useEffect(() => { if (value) setForm({ ...emptyJob, ...value }); }, [value]);
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); onSave(form); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); onOpenChange(v); }}>
      <DialogContent data-testid="admin-job-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-navy-800">{form.id ? "Edit Job" : "Add Job"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <F label="Title*"><Input required value={form.title} onChange={on("title")} data-testid="job-form-title" /></F>
          <F label="Location*"><Input required value={form.location} onChange={on("location")} data-testid="job-form-location" /></F>
          <F label="Salary*"><Input required value={form.salary} onChange={on("salary")} data-testid="job-form-salary" /></F>
          <F label="Experience*"><Input required value={form.experience} onChange={on("experience")} data-testid="job-form-experience" /></F>
          <F label="Qualification*"><Input required value={form.qualification} onChange={on("qualification")} data-testid="job-form-qualification" /></F>
          <F label="Employment Type*">
            <select value={form.employment_type} onChange={on("employment_type")} data-testid="job-form-employment-type" className="h-10 w-full bg-white border border-input rounded-md px-3 text-sm">
              {["Full-time", "Part-time", "Contract", "Temporary", "Internship"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </F>
          <F label="Shift*">
            <select value={form.shift} onChange={on("shift")} data-testid="job-form-shift" className="h-10 w-full bg-white border border-input rounded-md px-3 text-sm">
              {["Day", "Night", "Rotational", "Flexible"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </F>
          <F label="Industry*">
            <select required value={form.industry} onChange={on("industry")} data-testid="job-form-industry" className="h-10 w-full bg-white border border-input rounded-md px-3 text-sm">
              <option value="">Select</option>
              {INDUSTRIES.map((i) => <option key={i.name}>{i.name}</option>)}
            </select>
          </F>
          <div className="sm:col-span-2">
            <F label="Description*"><Textarea required rows={4} value={form.description} onChange={on("description")} data-testid="job-form-description" /></F>
          </div>
          <div className="sm:col-span-2 flex items-center gap-2">
            <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="job-form-active" />
            <Label>Active (visible on public jobs page)</Label>
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-navy-800 hover:bg-navy-700 text-white" data-testid="job-form-submit">{form.id ? "Update Job" : "Create Job"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-navy-800 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
